import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/shared/lib/api";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";

export default function ResetPassword() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) { toast.error("Passwords do not match"); return; }
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please sign in.");
      nav("/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-24" data-testid="reset-password-page">
      <div className="label-xs text-[#ed6500] mb-6">New password</div>
      <h1 className="font-serif font-light text-5xl leading-[0.95] tracking-tighter mb-6">Set a new password.</h1>
      {!token ? (
        <div className="border hairline bg-white p-6 text-sm text-[#515E7B]" data-testid="reset-no-token">
          This reset link is missing a token. <Link to="/forgot-password" className="text-[#ed6500] hover:underline">Request a new link</Link>.
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-8">
          <div>
            <div className="label-xs mb-3">New password</div>
            <div className="relative">
              <input
                className="input-line pr-10"
                type={showPw ? "text" : "password"}
                minLength={6} required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="reset-password-input"
              />
              <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#515E7B] hover:text-[#0A1128] p-1" tabIndex={-1} data-testid="reset-password-toggle">
                {showPw ? <EyeOff size={16}/> : <Eye size={16}/>}
              </button>
            </div>
          </div>
          <div>
            <div className="label-xs mb-3">Confirm password</div>
            <input
              className="input-line"
              type={showPw ? "text" : "password"}
              minLength={6} required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-password-confirm"
            />
          </div>
          <button disabled={busy} className="btn-primary text-xs tracking-widest uppercase disabled:opacity-60" data-testid="reset-submit">
            {busy ? "Updating…" : "Update password"}
          </button>
        </form>
      )}
    </div>
  );
}
